import { useCallback, useEffect, useMemo, useState } from 'react';

import { type FilterOption } from '@frontend/api';
import type { NumericFilterCondition } from '@frontend/api';
import {
  Flex,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  SelectComponents,
} from '@frontend/legacy-components';

export type NumericFilterValue = {
  operator: NumericFilterCondition;
  value: number | null;
};

export type NumericFilterProps = {
  value: NumericFilterValue | null;
  onChange: (value: NumericFilterValue) => void;
  options: FilterOption[];
  operatorSelectRef?: React.Ref<unknown>;
  operatorSelectComponents?: {
    Control?: typeof SelectComponents.Control;
  };
  isPopoverOverlayEnabled?: boolean;
};

export const NumericFilter = ({
  value,
  onChange,
  options,
  operatorSelectRef,
  operatorSelectComponents,
  isPopoverOverlayEnabled,
}: NumericFilterProps) => {
  const [inputValue, setInputValue] = useState<string>(
    value?.value?.toString() ?? '',
  );

  // Sync input value when external value changes
  useEffect(() => {
    setInputValue(value?.value?.toString() ?? '');
  }, [value?.value]);

  // Default to first option if no value is set
  const defaultOperator = options[0]?.value as NumericFilterCondition;

  const workingValue: NumericFilterValue = useMemo(
    () =>
      value ?? {
        operator: defaultOperator,
        value: null,
      },
    [value, defaultOperator],
  );

  const selectedOperator = options.find(
    (op) => op.value === workingValue.operator,
  );

  const handleOperatorChange = useCallback(
    (selectedOption: FilterOption | null) => {
      if (selectedOption) {
        onChange({
          ...workingValue,
          operator: selectedOption.value as NumericFilterCondition,
        });
      }
    },
    [workingValue, onChange],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    [setInputValue],
  );

  const handleInputBlur = useCallback(() => {
    const numValue = inputValue === '' ? null : parseFloat(inputValue);
    onChange({
      ...workingValue,
      value: numValue !== null && !isNaN(numValue) ? numValue : null,
    });
  }, [inputValue, workingValue, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.currentTarget.blur();
      }
    },
    [],
  );

  return (
    <Flex width="100%" gap={0}>
      <InputGroup>
        <InputLeftAddon flexBasis="180px" flexShrink={0} p={0} borderWidth={0}>
          <Select
            ref={operatorSelectRef}
            options={options}
            value={selectedOperator}
            onChange={handleOperatorChange}
            isClearable={false}
            isMulti={false}
            isPopoverOverlayEnabled={isPopoverOverlayEnabled}
            components={operatorSelectComponents}
            placeholder="Select operator"
          />
        </InputLeftAddon>
        <Input
          type="number"
          min={1}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter value"
        />
      </InputGroup>
    </Flex>
  );
};
